import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

export interface OdooSigner {
  role: 'SalesPerson' | 'Customer';
  name: string;
  email: string;
  order: number;
}

export interface OdooTemplateDiscriminators {
  channel?: string;
  lead_type?: string;
  is_financed: boolean;
  financing_provider?: string;
  is_renewal?: boolean;
  is_showroom: boolean;
  is_dealer_program: boolean;
}

export interface OdooSigningBlock {
  mode: 'in_person' | 'remote' | 'va';
  signers: OdooSigner[];
  return_url: string;
  template_discriminators: OdooTemplateDiscriminators;
}

export interface OdooSignCreateRequest {
  guid: string;
  uid?: number;
  signType?: string;
  signing?: OdooSigningBlock;
  [key: string]: any;
}

export interface OdooSignCreateResponse {
  uuid: string;
  state: 'awaiting_signature' | 'failed' | string;
  crm_id?: number;
  order_id?: number;
  request_id?: number;
  signer_urls?: {
    SalesPerson?: string;
    Customer?: string;
  };
  reason?: string;
  message?: string;
  confirm_wait_seconds?: number;
}

export interface OdooConfirmationRequest {
  payment_confirmation_number: string;
  total_paid?: string;
  /** Receipt data (Cheatsheet §9 item 34): payment time with Z or an offset, masked instrument, instrument type */
  tx_date?: string;
  funding_account?: string;
  funding_type?: string;
}

export interface OdooConfirmationResponse {
  state: 'ok' | 'failed';
  reason?: string;
  uuid: string;
  order_id?: number;
  request_id?: number;
  payment_state?: string;
  macro_state?: string;
  sign_confirm_state?: string;
  signer_urls?: {
    Customer?: string;
    SalesPerson?: string;
  };
  message?: string;
}

export interface OdooContractStatusResponse {
  uuid: string;
  state?: 'ok' | 'failed' | string;
  order_id?: number;
  request_id?: number;
  signer_urls?: {
    SalesPerson?: string;
    Customer?: string;
  };
  macro_state?: string;
  sign_state?: string;
  sale_order_state?: string;
  sign_confirm_state?: string;
  payment_state?: string;
  project_task_id?: number[];
  withdrawn_on?: string | null;
  confirm_wait_seconds?: number;
  signed_on?: string;
  failure_reason?: string;
  reason?: string;
  message?: string;
}

export interface OdooWithdrawRequest {
  uid?: number;
  withdraw_reason: 'workflow_restart' | 'customer_declined';
}

export interface OdooWithdrawResponse {
  state: 'ok' | 'failed';
  reason?: string;
  uuid: string;
  crm_id?: number;
  order_id?: number;
  request_id?: number;
  macro_state?: string;
  sign_state?: string;
  sale_order_state?: string;
  withdrawn_on?: string;
  signer_urls?: { [key: string]: string };
  message?: string;
}

export interface OdooBypassRequest {
  uid?: number;
  approved_by: string;
  reason: string;
}

export interface OdooBypassResponse {
  state: 'ok' | 'failed';
  reason?: string;
  uuid: string;
  payment_state?: string;
  signer_urls?: {
    Customer?: string;
    SalesPerson?: string;
  };
  message?: string;
}

export interface OdooLatePaymentEvidence {
  filename: string;
  mimetype: 'application/pdf' | 'image/jpeg' | 'image/png' | string;
  content_base64: string;
}

export interface OdooLatePaymentRequest {
  uid?: number;
  payment_confirmation_number: string;
  total_paid: string | number;
  tx_date: string;
  acquirer: 'authz' | 'aci';
  funding_account?: string;
  funding_type?: string;
  evidence?: OdooLatePaymentEvidence;
}

export interface OdooLatePaymentResponse {
  state: 'ok' | 'failed';
  reason?: string;
  uuid: string;
  late_payment?: boolean;
  payment_state?: string;
  message?: string;
}

@Injectable({
  providedIn: 'root',
})
export class OdooSignService {
  /** Latest confirm_wait_seconds read from Odoo M1 or status replies (default 300 s, Cheatsheet §6, §9 item 30). */
  confirmWaitSeconds: number = 300;

  constructor(private _http: HttpClient) { }

  /**
   * M1: Create draft contract in Odoo and retrieve signer URLs
   * POST /api/salespro/odoosign/contract
   */
  createContract(payload: OdooSignCreateRequest): Promise<OdooSignCreateResponse> {
    const url = `/api/salespro/odoosign/contract`;
    return this._http.post<OdooSignCreateResponse>(url, payload).toPromise().then((res) => {
      if (res?.confirm_wait_seconds && res.confirm_wait_seconds > 0) {
        this.confirmWaitSeconds = res.confirm_wait_seconds;
      }
      return res;
    });
  }

  /**
   * Payment Confirmation Callback: Late-bind payment receipt & release Customer signer URL
   * POST /api/salespro/odoosign/contract/confirmations/:uuid
   */
  confirmPayment(customerGuid: string, payload: OdooConfirmationRequest,remote = ''): Promise<OdooConfirmationResponse> {
    let url = `/api/salespro/odoosign/contract/confirmations/${customerGuid}`;
    if (remote === 'remote') {
      url = `/api/salespro/odoosign/remote/contract/confirmations/${customerGuid}`;
    }
    return this._http.post<OdooConfirmationResponse>(url, payload).toPromise();
  }

  /**
   * Bypass Payment: Out-of-band pre-approved skip payment (ADR-0022 / Contract §3.7)
   * POST /api/salespro/odoosign/contract/bypass/:uuid
   */
  bypassPayment(uuid: string, payload: OdooBypassRequest): Promise<OdooBypassResponse> {
    const url = `/api/salespro/odoosign/contract/bypass/${uuid}`;
    return this._http.post<OdooBypassResponse>(url, payload).toPromise();
  }

  /**
   * Late Payment: Record money collected after a bypass (ADR-0022 / Contract §3.8)
   * POST /api/salespro/odoosign/contract/late-payment/:uuid
   */
  latePayment(uuid: string, payload: OdooLatePaymentRequest): Promise<OdooLatePaymentResponse> {
    const url = `/api/salespro/odoosign/contract/late-payment/${uuid}`;
    return this._http.post<OdooLatePaymentResponse>(url, payload).toPromise();
  }

  /**
   * Contract Status Inquiry (Connection-loss & poll fallback)
   * GET /api/salespro/odoosign/contract/status?uuid=<uuid>&uid=<uid>
   */
  getContractStatus(customerGuid: string, uid?: number, remote = ''): Promise<OdooContractStatusResponse> {
    let url = `/api/salespro/odoosign/contract/status/${customerGuid}`;
    if (remote === 'remote') {
      url = `/api/salespro/odoosign/remote/contract/status/${customerGuid}`;
    }
    if (uid) {
      url += `?uid=${uid}`;
    }
    return this._http.get<OdooContractStatusResponse>(url).toPromise().then((res) => {
      if (res?.confirm_wait_seconds && res.confirm_wait_seconds > 0) {
        this.confirmWaitSeconds = res.confirm_wait_seconds;
      }
      return res;
    });
  }

  /**
   * Deal Withdrawal (ADR-0021 / Contract §3.6)
   * POST /api/salespro/odoosign/contract/withdraw/:uuid
   */
  withdrawContract(uuid: string, payload: OdooWithdrawRequest): Promise<OdooWithdrawResponse> {
    const url = `/api/salespro/odoosign/contract/withdraw/${uuid}`;
    return this._http.post<OdooWithdrawResponse>(url, payload).toPromise();
  }
}
